import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';

// Import our custom theme
import { havenTheme } from './theme/theme';

// Import analytics utilities
import { initMixpanel, trackPageView } from './utils/analytics';
import RouteMetaManager from './components/seo/RouteMetaManager';
import ScrollToTop from './components/common/ScrollToTop';
import MailchimpTracking from './components/integrations/MailchimpTracking';

// Import cookie consent manager
import CookieConsentManager from './components/cookie-consent/CookieConsentManager';

// Import components
import Layout from './components/layout/Layout';

// NOTE: React Query removed - no pages currently use useQuery/useMutation
// This saves ~50-80KB from bundle AND eliminates blocking time from lazy loading overhead

// HomePage is statically imported so the client bundle ships its code
// alongside the entry. Lazy-loading HomePage caused a React #418 hydration
// mismatch in the prerendered '/' route: the SSR HTML had the rendered
// HomePage tree, but the client's first render was still <Suspense
// fallback={Loader}> while the chunk loaded, so React saw a structural
// HTML mismatch on hydration. Bundling HomePage with the entry keeps
// hydration synchronous and matches the prerendered markup exactly.
import HomePage from './pages/home/HomePage';

const HomeownerStartPage = lazy(() => import('./pages/home/HomeownerStartPage'));
const HomeownerStorePageWrapper = lazy(() => import('./pages/homeowner-store').then(module => ({ default: module.HomeownerStorePage })));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const BlogPageWrapper = lazy(() => import('./pages/blog').then(module => ({ default: module.BlogPage })));
const BlogPostPageWrapper = lazy(() => import('./pages/blog').then(module => ({ default: module.BlogPostPage })));
const SolutionsPage = lazy(() => import('./pages/solutions/SolutionsPage'));
const ResidentialVentilationPage = lazy(() => import('./pages/solutions/ResidentialVentilationPage'));
const ActiveAirManagementPage = lazy(() => import('./pages/solutions/ActiveAirManagementPage'));
const RadonMitigationPage = lazy(() => import('./pages/solutions/RadonMitigationPage'));
const ResourcesPage = lazy(() => import('./pages/resources/ResourcesPage'));
const AboutPage = lazy(() => import('./pages/about/AboutPage'));
const KevinHartPage = lazy(() => import('./pages/about/KevinHartPage'));
const RoomMonitorPage = lazy(() => import('./pages/products/RoomMonitorPage'));
const WildfireInfiltrationPage = lazy(() => import('./pages/solutions/WildfireInfiltrationPage'));
const HavenIaqStartPage = lazy(() => import('./pages/HavenIaqStartPage'));
const EmailTestPage = lazy(() => import('./pages/EmailTestPage'));
const RoomInstallPage = lazy(() => import('./pages/RoomInstallPage'));
const ProStorePage = lazy(() => import('./pages/ProStorePage'));
const CentralAirMonitorPage = lazy(() => import('./pages/products/CentralAirMonitorPage'));
const CentralControllerPage = lazy(() => import('./pages/CentralControllerPage'));
const MyPortalPage = lazy(() => import('./pages/MyPortalPage'));
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));
const BecomeProPage = lazy(() => import('./pages/pro/BecomeProPage'));
// Disabled sale pages - kept for reference but not accessible via routes
// const SeptemberClearance2025Page = lazy(() => import('./pages/sales/SeptemberClearance2025Page'));
// const HalloweenSaleProPage = lazy(() => import('./pages/sales/HalloweenSaleProPage'));
// const HalloweenSaleHomeownerPage = lazy(() => import('./pages/sales/HalloweenSaleHomeownerPage'));

const FallSalePage = lazy(() => import('./pages/sales/FallSalePage'));
const RoomMonitorProDiscountPage = lazy(() => import('./pages/sales/RoomMonitorProDiscountPage'));
const BlackFridaySaleProPage = lazy(() => import('./pages/sales/BlackFridaySaleProPage'));
const BlackFridaySaleHomeownerPage = lazy(() => import('./pages/sales/BlackFridaySaleHomeownerPage'));
const ReturnPolicyPage = lazy(() => import('./pages/ReturnPolicyPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const EULAPage = lazy(() => import('./pages/EULAPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const HomeownerReportRoutes = lazy(() => import('./routes/HomeownerReportRoutes'));
const IAQReportRoute = lazy(() => import('./routes/IAQReportRoute'));
const HangoutPage = lazy(() => import('./pages/hangout/HangoutPage'));
const LiveRoomPage = lazy(() => import('./pages/live-room/LiveRoomPage'));
type SupportPagesModule = typeof import('./pages/support');
let preloadedSupportPages: SupportPagesModule | null = null;
const loadSupportPages = () => import('./pages/support').then(module => {
  preloadedSupportPages = module;
  return module;
});

export const preloadSupportRoutes = loadSupportPages;

const LazySupportHubPage = lazy(() => loadSupportPages().then(module => ({ default: module.SupportHubPage })));
const LazySupportArticlePage = lazy(() => loadSupportPages().then(module => ({ default: module.SupportArticlePage })));
const LazySupportContactPage = lazy(() => loadSupportPages().then(module => ({ default: module.SupportContactPage })));
const LazySupportInstallationPage = lazy(() => loadSupportPages().then(module => ({ default: module.SupportInstallationPage })));

const SupportHubPage: React.FC<React.ComponentProps<SupportPagesModule['SupportHubPage']>> = props => {
  const Component = preloadedSupportPages?.SupportHubPage;
  return Component ? <Component {...props} /> : <LazySupportHubPage {...props} />;
};
const SupportArticlePage: React.FC<React.ComponentProps<SupportPagesModule['SupportArticlePage']>> = props => {
  const Component = preloadedSupportPages?.SupportArticlePage;
  return Component ? <Component {...props} /> : <LazySupportArticlePage {...props} />;
};
const SupportContactPage: React.FC<React.ComponentProps<SupportPagesModule['SupportContactPage']>> = props => {
  const Component = preloadedSupportPages?.SupportContactPage;
  return Component ? <Component {...props} /> : <LazySupportContactPage {...props} />;
};
const SupportInstallationPage: React.FC = () => {
  const Component = preloadedSupportPages?.SupportInstallationPage;
  return Component ? <Component /> : <LazySupportInstallationPage />;
};

// Loading component for lazy-loaded pages
const PageLoader = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '50vh' 
    }}
  >
    <CircularProgress />
  </Box>
);

// iOS App Store redirect component
const IOSAppRedirect = () => {
  useEffect(() => {
    window.location.replace('https://apps.apple.com/us/app/haven-iaq/id1527338018');
  }, []);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}
    >
      <CircularProgress />
    </Box>
  );
};

// Android Google Play Store redirect component
const AndroidAppRedirect = () => {
  useEffect(() => {
    window.location.replace('https://play.google.com/store/apps/details?id=com.tzoa.haveniaq&hl=en_CA');
  }, []);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}
    >
      <CircularProgress />
    </Box>
  );
};

// IFTTT integration redirect — the original blog post was never migrated
// to MDX, but the IFTTT integration itself is still live. Send these
// legacy WordPress dated URLs to the HAVEN IAQ Applet listing on IFTTT.
const IFTTTRedirect = () => {
  useEffect(() => {
    window.location.replace('https://ifttt.com/haven_iaq');
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh'
      }}
    >
      <CircularProgress />
    </Box>
  );
};

// Component to track page views on route changes
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view when route changes
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

// The live-room URL contains a temporary-assessment access code. Keep that
// credential out of analytics and avoid interrupting a projected presentation
// with marketing or cookie-consent UI.
function OptionalGlobalExperiences() {
  const location = useLocation();
  if (location.pathname.startsWith('/live-room')) return null;

  return (
    <>
      <PageViewTracker />
      <CookieConsentManager />
      <MailchimpTracking />
    </>
  );
}

const legacyRedirects: Record<string, string> = {
  '/news': '/blog',
  '/ios': '/ios-app',
  '/install-controller': '/resources',
  '/home-air-purifier': '/active-air-management',
  '/gm-display-q1-2021-instagram': '/',
  '//room-install': '/room-install',
};

const LegacyRedirectOrNotFound = () => {
  const location = useLocation();
  const normalizedPath = location.pathname.length > 1
    ? location.pathname.replace(/\/+$/, '').toLowerCase()
    : location.pathname;
  const destination = legacyRedirects[normalizedPath];

  if (destination) {
    return (
      <Navigate
        to={{ pathname: destination, search: location.search, hash: location.hash }}
        replace
      />
    );
  }

  return <NotFoundPage />;
};

if (typeof window === 'undefined' || !window.location.pathname.startsWith('/live-room')) {
  void initMixpanel();
}

const AppReadyMarker = () => {
  useEffect(() => {
    document.documentElement.dataset.havenHydrated = 'true';
  }, []);

  return null;
};

function App() {
  return (
    <ThemeProvider theme={havenTheme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <RouteMetaManager />
        <OptionalGlobalExperiences />
        <Suspense fallback={<PageLoader />}>
          <Routes>
              <Route path="/live-room/:accessCode?" element={<LiveRoomPage />} />
              <Route path="/report/*" element={<HomeownerReportRoutes />} />
              {/* iOS App Store redirect */}
              <Route path="/ios-app" element={<IOSAppRedirect />} />
              <Route path="/ios-pro" element={<IOSAppRedirect />} />
              <Route path="/ios-pro/" element={<IOSAppRedirect />} />
              <Route path="/dvt-qsg-ios" element={<Navigate to="/ios-app" replace />} />
              {/* Android Google Play Store redirect */}
              <Route path="/android-app" element={<AndroidAppRedirect />} />
              <Route path="/android-pro" element={<AndroidAppRedirect />} />
              <Route path="/android-pro/" element={<AndroidAppRedirect />} />
              <Route path="/dvt-qsg-android" element={<Navigate to="/android-app" replace />} />
              {/* Internal redirects */}
              <Route path="/install-monitor" element={<Navigate to="/resources" replace />} />
              <Route path="/dvt-qsg-install-cac" element={<Navigate to="/resources" replace />} />
              {/* Halloween sale routes disabled - pages kept for reference */}
              {/* <Route path="/halloween-sale-pro" element={<HalloweenSaleProPage />} /> */}
              {/* <Route path="/halloween-sale-homeowner" element={<HalloweenSaleHomeownerPage />} /> */}
              <Route path="/black-friday-sale-pro" element={<BlackFridaySaleProPage />} />
              <Route path="/black-friday-sale-homeowner" element={<BlackFridaySaleHomeownerPage />} />
              
              {/* All other routes with Layout */}
              <Route path="/*" element={
                <Layout>
                  <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/homeowner-start" element={<HomeownerStartPage />} />
              <Route path="/homeowners" element={<Navigate to="/homeowner-start" replace />} />
              <Route path="/homeowner-store" element={<HomeownerStorePageWrapper />} />
              <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
              <Route path="/email-test" element={import.meta.env.DEV ? <EmailTestPage /> : <Navigate to="/" replace />} />
              <Route path="/room-monitor" element={<RoomMonitorPage />} />
              <Route path="/room-install" element={<RoomInstallPage />} />
              <Route path="/central-air-monitor" element={<CentralAirMonitorPage />} />
              <Route path="/central-controller" element={<CentralControllerPage />} />

              <Route path="/solutions" element={<SolutionsPage />} />
              <Route path="/residential-demand-control-ventilation-for-humid-climates" element={<ResidentialVentilationPage />} />
              <Route path="/residential-demand-control-ventilation" element={<ResidentialVentilationPage />} />
              <Route path="/active-air-management" element={<ActiveAirManagementPage />} />
              <Route path="/haven-products" element={<ActiveAirManagementPage />} />
              <Route path="/smart-home-radon-mitigation" element={<RadonMitigationPage />} />
              <Route path="/wildfire-infiltration-checklist" element={<WildfireInfiltrationPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/about/kevinrhart" element={<KevinHartPage />} />
              <Route path="/about/kevinrhart/" element={<KevinHartPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/support" element={<SupportHubPage audience="consumer" />} />
              <Route path="/support/articles/:articleKey" element={<SupportArticlePage audience="consumer" />} />
              <Route path="/support/contact" element={<SupportContactPage audience="consumer" />} />
              <Route path="/support/pro" element={<SupportHubPage audience="pro" />} />
              <Route path="/support/pro/articles/:articleKey" element={<SupportArticlePage audience="pro" />} />
              <Route path="/support/pro/contact" element={<SupportContactPage audience="pro" />} />
              <Route path="/support/pro/installation" element={<SupportInstallationPage />} />
              <Route path="/return-policy" element={<ReturnPolicyPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/eula" element={<EULAPage />} />
              <Route path="/tos" element={<TermsOfServicePage />} />
              <Route path="/terms" element={<Navigate to="/tos" replace />} />
              <Route path="/terms-of-service" element={<Navigate to="/tos" replace />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/become-a-pro" element={<BecomeProPage />} />
              <Route path="/haven-iaq-start" element={<HavenIaqStartPage />} />
              <Route path="/prostore" element={<ProStorePage />} />
              <Route path="/my-portal" element={<MyPortalPage />} />
                        {/* September clearance sale route disabled - page kept for reference */}
                        {/* <Route path="/inventory_clearance_sale" element={<SeptemberClearance2025Page />} /> */}
                        <Route path="/hangout" element={<HangoutPage />} />
                        <Route path="/fall-sale" element={<FallSalePage />} />
                        <Route path="/room-monitor-pro-discount" element={<RoomMonitorProDiscountPage />} />
                        <Route path="/iaq-report" element={<IAQReportRoute />} />
                        <Route path="/assessment/:reportId" element={<IAQReportRoute />} />
              
              {/* Blog routes — all posts are MDX, resolved by the dynamic
                  /blog/:slug route. Redirects below cover retired slugs. */}
              <Route path="/blog" element={<BlogPageWrapper />} />
              <Route path="/blog/haven-meets-tricklestar" element={<Navigate to="/blog" replace />} />
              {/* The two wildfire posts were merged into one — point the
                  pro-angled slug at the merged post. */}
              <Route path="/blog/haven-for-hvac-wildfire-season" element={<Navigate to="/blog/haven-for-wildfire-smoke" replace />} />
              <Route path="/blog/:slug" element={<BlogPostPageWrapper />} />
              
              {/* Old WordPress URL redirects - handle date-based URLs */}
              <Route path="/2021/06/haven-for-wildfire-smoke" element={<Navigate to="/blog/haven-for-wildfire-smoke" replace />} />
              <Route path="/2021/06/haven-for-wildfire-smoke/" element={<Navigate to="/blog/haven-for-wildfire-smoke" replace />} />
              
              {/* HVAC is Hard - November 2020 */}
              <Route path="/2020/11/hvac-is-hard-iaq-is-harder" element={<Navigate to="/blog/hvac-is-hard-iaq-is-harder" replace />} />
              <Route path="/2020/11/hvac-is-hard-iaq-is-harder/" element={<Navigate to="/blog/hvac-is-hard-iaq-is-harder" replace />} />
              <Route path="/2021/06/hvac-is-hard-iaq-is-harder" element={<Navigate to="/blog/hvac-is-hard-iaq-is-harder" replace />} />
              <Route path="/2021/06/hvac-is-hard-iaq-is-harder/" element={<Navigate to="/blog/hvac-is-hard-iaq-is-harder" replace />} />
              
              {/* Wildfire Season - Both June and July 2021 */}
              <Route path="/2021/06/haven-for-hvac-wildfire-season" element={<Navigate to="/blog/haven-for-hvac-wildfire-season" replace />} />
              <Route path="/2021/06/haven-for-hvac-wildfire-season/" element={<Navigate to="/blog/haven-for-hvac-wildfire-season" replace />} />
              <Route path="/2021/07/haven-for-hvac-wildfire-season" element={<Navigate to="/blog/haven-for-hvac-wildfire-season" replace />} />
              <Route path="/2021/07/haven-for-hvac-wildfire-season/" element={<Navigate to="/blog/haven-for-hvac-wildfire-season" replace />} />
              
              {/* Legacy WordPress posts that were NOT migrated to MDX. The
                  original /blog/<slug> destinations don't exist anymore, so
                  these redirects now point at the closest live page that
                  still serves the user's likely intent — avoids dead-ending
                  on a missing blog post. */}

              {/* Air Pollution and Asthma — post not migrated; closest live
                  content is the blog index where current health/IAQ posts
                  live (PM, IAQ intro). */}
              <Route path="/2021/05/air-pollution-and-asthma" element={<Navigate to="/blog" replace />} />
              <Route path="/2021/05/air-pollution-and-asthma/" element={<Navigate to="/blog" replace />} />
              <Route path="/2021/06/air-pollution-and-asthma" element={<Navigate to="/blog" replace />} />
              <Route path="/2021/06/air-pollution-and-asthma/" element={<Navigate to="/blog" replace />} />

              {/* October 2020 Software Updates — point at /resources (current
                  release notes & docs hub). */}
              <Route path="/2020/10/haven-iaq-october-2020-software-updates" element={<Navigate to="/resources" replace />} />
              <Route path="/2020/10/haven-iaq-october-2020-software-updates/" element={<Navigate to="/resources" replace />} />

              {/* Intro to Central Air Monitor — product page is the natural
                  successor. */}
              <Route path="/2020/11/introduction-to-the-haven-central-air-monitor" element={<Navigate to="/central-air-monitor" replace />} />
              <Route path="/2020/11/introduction-to-the-haven-central-air-monitor/" element={<Navigate to="/central-air-monitor" replace />} />
              <Route path="/2020/10/introduction-to-the-haven-central-air-monitor" element={<Navigate to="/central-air-monitor" replace />} />
              <Route path="/2020/10/introduction-to-the-haven-central-air-monitor/" element={<Navigate to="/central-air-monitor" replace />} />

              {/* IFTTT post — article wasn't migrated to MDX, but the IFTTT
                  integration is still live on IFTTT. Send users to the HAVEN
                  IAQ Applet listing. (Netlify _redirects handles direct hits;
                  this client-side route covers SPA navigation parity.) */}
              <Route path="/2020/12/haven-iaq-ifttt-our-first-step-on-the-journey-to-automate-your-air-quality" element={<IFTTTRedirect />} />
              <Route path="/2020/12/haven-iaq-ifttt-our-first-step-on-the-journey-to-automate-your-air-quality/" element={<IFTTTRedirect />} />
              <Route path="/2020/09/haven-iaq-ifttt-our-first-step-on-the-journey-to-automate-your-air-quality" element={<IFTTTRedirect />} />
              <Route path="/2020/09/haven-iaq-ifttt-our-first-step-on-the-journey-to-automate-your-air-quality/" element={<IFTTTRedirect />} />
              
              {/* Additional blog posts */}
              <Route path="/2021/11/indoor-air-quality-introduction" element={<Navigate to="/blog/indoor-air-quality-introduction" replace />} />
              <Route path="/2021/11/indoor-air-quality-introduction/" element={<Navigate to="/blog/indoor-air-quality-introduction" replace />} />
              <Route path="/2022/01/health-effects-of-particulate-matter" element={<Navigate to="/blog/health-effects-of-particulate-matter" replace />} />
              <Route path="/2022/01/health-effects-of-particulate-matter/" element={<Navigate to="/blog/health-effects-of-particulate-matter" replace />} />
              <Route path="/2022/10/haven-meets-tricklestar-wireless-iaq-integrations" element={<Navigate to="/blog" replace />} />
              <Route path="/2022/10/haven-meets-tricklestar-wireless-iaq-integrations/" element={<Navigate to="/blog" replace />} />
              <Route path="/2024/03/haven-and-ecosense-radon-solutions" element={<Navigate to="/blog/haven-and-ecosense-radon-solutions" replace />} />
              <Route path="/2024/03/haven-and-ecosense-radon-solutions/" element={<Navigate to="/blog/haven-and-ecosense-radon-solutions" replace />} />
              <Route path="/2024/09/no-one-cares-about-residential-controls-an-open-letter" element={<Navigate to="/blog/no-one-cares-about-residential-controls" replace />} />
              <Route path="/2024/09/no-one-cares-about-residential-controls-an-open-letter/" element={<Navigate to="/blog/no-one-cares-about-residential-controls" replace />} />
              <Route path="/blog/no-one-cares-about-residential-controls-an-open-letter" element={<Navigate to="/blog/no-one-cares-about-residential-controls" replace />} />

              <Route path="*" element={<LegacyRedirectOrNotFound />} />
              
                  </Routes>
                </Layout>
              } />
          </Routes>
          <AppReadyMarker />
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
